// ============================================================
// PARRY WEAPON - Pure functions for defender parry weapon
// selection and modifier calculation. Zero React dependency.
// ============================================================
import type { Target, DefenderWeapon } from '../types';
import { hasWS } from './weaponSpecials';

/**
 * Find the index of the first melee weapon that can parry (not Unwieldy).
 * Ranged weapons cannot be used to parry. Returns -1 if none found.
 */
export function findParryWeaponIndex(weapons: DefenderWeapon[]): number {
  for (let i = 0; i < weapons.length; i++) {
    const w = weapons[i];
    if (w.type === 'melee' && !hasWS(w.specialRules, 'Unwieldy')) {
      return i;
    }
  }
  return -1;
}

/**
 * Calculate the cumulative parry modifier from weapon special rules.
 * Balanced → +10, Unbalanced → -10, Defensive → +15.
 */
export function calcWeaponParryMod(specialRules: string[]): number {
  let mod = 0;
  if (hasWS(specialRules, 'Balanced')) mod += 10;
  if (hasWS(specialRules, 'Unbalanced')) mod -= 10;
  if (hasWS(specialRules, 'Defensive')) mod += 15;
  return mod;
}

/**
 * Re-resolve parry stats from a target's defender weapons array.
 * Used when weapons are reordered in the Gear tab - recalculates
 * which weapon is the parry weapon and applies its modifiers.
 *
 * Returns partial target fields to spread into the target object.
 */
export function reResolveParryFromWeapons(
  target: Target,
): Pick<Target, 'parry' | 'defenderWeapons' | 'parryBaseSkill'> {
  const weapons = target.defenderWeapons || [];
  const baseSkill = target.parryBaseSkill || 0;
  return {
    defenderWeapons: weapons,
    parryBaseSkill: baseSkill,
    parry: resolvedParry(target.parry, weapons, baseSkill),
  };
}

/**
 * The parry the weapons give: the first that can parry, with its
 * modifiers; none that can - parry disabled; no weapons at all - the
 * existing parry kept (manual targets).
 */
function resolvedParry(existingParry: Target['parry'], weapons: DefenderWeapon[], baseSkill: number): Target['parry'] {
  const idx = findParryWeaponIndex(weapons);
  if (idx >= 0) {
    const pw = weapons[idx];
    return {
      ...existingParry,
      canParry: existingParry.canParry, // preserve trained state
      weaponSkill: baseSkill + calcWeaponParryMod(pw.specialRules),
      hasPowerField: hasWS(pw.specialRules, 'PowerField'),
      parryWeaponName: pw.name,
      parryWeaponSpecials: [...pw.specialRules],
    };
  }
  if (weapons.length === 0) return existingParry;
  return {
    ...existingParry,
    canParry: false,
    weaponSkill: baseSkill,
    hasPowerField: false,
    parryWeaponName: undefined,
    parryWeaponSpecials: undefined,
  };
}
